import { createTheme, alpha } from '@mui/material/styles'
import type {} from '@mui/x-date-pickers/themeAugmentation'
import { typography } from './typography'
import { success, warning, error, info } from './palette'
import type { BrandKit } from '@/branding/brand.types'
import { DEFAULT_BRAND } from '@/branding/brandDetection'

/** Status colours and neutrals are shared across brands; only the primary and
 *  secondary hues come from the organization's brand kit. */
const buildPalette = (mode: 'light' | 'dark', brand: BrandKit) => {
  const { primary, secondary } = brand.palette

  if (mode === 'light') {
    return {
      mode: 'light' as const,
      primary: {
        main: primary.main,
        dark: primary.dark,
        light: primary.light,
        contrastText: primary.contrastText,
      },
      secondary: {
        main: secondary.main,
        dark: secondary.dark,
        light: secondary.light,
        contrastText: secondary.contrastText,
      },
      success: { main: success.main, light: success.light, dark: success.dark },
      warning: { main: warning.main, light: warning.light, dark: warning.dark },
      error: { main: error.main, light: error.light, dark: error.dark },
      info: { main: info.main, light: info.light, dark: info.dark },
      background: { default: '#F8FAFC', paper: '#FFFFFF' },
      text: brand.palette.text ?? { primary: '#0F172A', secondary: '#64748B' },
      divider: '#E2E8F0',
    }
  }

  return {
    mode: 'dark' as const,
    primary: {
      main: primary.light,
      dark: primary.main,
      light: primary.lighter ?? primary.light,
      contrastText: primary.contrastText,
    },
    secondary: {
      main: secondary.light,
      dark: secondary.main,
      light: secondary.lighter ?? secondary.light,
      contrastText: secondary.contrastText,
    },
    success: { main: '#4ADE80' },
    warning: { main: '#FBBF24' },
    error: { main: '#F87171' },
    info: { main: '#60A5FA' },
    background: { default: '#0F172A', paper: '#1E293B' },
    text: { primary: '#F1F5F9', secondary: '#94A3B8' },
    divider: '#334155',
  }
}

export const createAppTheme = (mode: 'light' | 'dark', brand: BrandKit = DEFAULT_BRAND) => {
  const { primary } = brand.palette
  const palette = buildPalette(mode, brand)

  return createTheme({
    typography: brand.typography
      ? { ...typography, fontFamily: brand.typography.fontFamily }
      : typography,
    palette,
    shape: { borderRadius: 10 },
    shadows: [
      'none',
      `0 1px 2px ${alpha('#000', 0.05)}`,
      `0 1px 3px ${alpha('#000', 0.08)}`,
      `0 4px 6px -1px ${alpha('#000', 0.08)}`,
      `0 10px 15px -3px ${alpha('#000', 0.08)}`,
      `0 20px 25px -5px ${alpha('#000', 0.1)}`,
      ...Array(19).fill('none'),
    ] as never,

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { scrollbarWidth: 'thin' },
          '#nprogress .bar': { background: primary.main },
          '#nprogress .peg': { boxShadow: `0 0 10px ${primary.main}, 0 0 5px ${primary.main}` },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '6.6px 16px',
            fontSize: '0.875rem',
            textTransform: 'none',
            fontWeight: 600,
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${primary.main}, ${primary.dark})`,
            '&:hover': {
              background: `linear-gradient(135deg, ${primary.dark}, ${primary.main})`,
            },
            /*
             * The gradient above uses the `background` shorthand, which MUI's
             * disabled state cannot undo — it only sets `background-color`. So
             * a disabled button kept its colour while its label greyed out to
             * near-unreadable. Dim the gradient instead and keep the label
             * white, so disabled reads as the same button, quieter.
             */
            '&.Mui-disabled': {
              background: `linear-gradient(135deg, ${primary.main}, ${primary.dark})`,
              color: '#fff',
              opacity: 0.45,
            },
          },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { border: `1px solid ${palette.divider}`, borderRadius: 12 },
        },
      },

      MuiChip: {
        styleOverrides: { root: { fontWeight: 500, fontSize: '0.75rem' } },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: { borderRight: `1px solid ${palette.divider}` },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            marginBottom: 2,
            '&.Mui-selected': {
              backgroundColor: alpha(primary.main, 0.12),
              color: primary.main,
              '&:hover': { backgroundColor: alpha(primary.main, 0.18) },
              '& .MuiListItemIcon-root': { color: primary.main },
            },
          },
        },
      },

      MuiTextField: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': { borderRadius: 8 },
          },
        },
      },

      MuiDialog: {
        defaultProps: { fullWidth: true, maxWidth: 'sm' },
        styleOverrides: { paper: { borderRadius: 12 } },
      },

      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },

      MuiTooltip: {
        defaultProps: { arrow: true, enterDelay: 300 },
        styleOverrides: { tooltip: { fontSize: '0.75rem', borderRadius: 6 } },
      },

      MuiAutocomplete: {
        defaultProps: { fullWidth: true, autoHighlight: true, openOnFocus: true },
        styleOverrides: {
          paper: {
            borderRadius: 8,
            border: `1px solid ${palette.divider}`,
            boxShadow: `0 4px 6px -1px ${alpha('#000', 0.08)}`,
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            border: `1px solid ${palette.divider}`,
            boxShadow: `0 4px 6px -1px ${alpha('#000', 0.08)}`,
          },
        },
      },

      MuiPickersPopper: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            border: `1px solid ${palette.divider}`,
            boxShadow: `0 4px 6px -1px ${alpha('#000', 0.08)}`,
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: 8, paddingTop: '6.6px', paddingBottom: '6.6px' },
          sizeSmall: { paddingTop: '4px', paddingBottom: '4px' },
          sizeLarge: { paddingTop: '10.2px', paddingBottom: '10.2px' },
        },
      },

      MuiToggleButton: {
        styleOverrides: {
          root: { paddingTop: '9.3px', paddingBottom: '9.3px' },
        },
      },

      MuiAppBar: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },

      MuiDivider: {
        styleOverrides: { root: { borderColor: palette.divider } },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontWeight: 400,
            fontSize: '0.875rem',
            '& .MuiFormLabel-asterisk': { color: error.main },
          },
        },
      },

      MuiFormHelperText: {
        styleOverrides: { root: { marginLeft: 2, fontSize: '0.75rem' } },
      },

      MuiDialogTitle: {
        styleOverrides: { root: { fontWeight: 600, fontSize: '1.125rem', padding: '16px 24px' } },
      },

      MuiDialogActions: {
        styleOverrides: { root: { padding: '12px 24px 16px' } },
      },

      MuiTab: {
        styleOverrides: {
          root: { fontSize: '0.875rem' },
        },
      },

      MuiTablePagination: {
        defaultProps: { rowsPerPageOptions: [10, 20, 50, 100] },
      },
    },
  })
}

// Default-brand themes. Nothing in src imports these any more — the PDF
// renderers now build a theme from the resolved brand — but they are kept as
// public exports so other branches of this white-label app keep building.
export const swaashTheme = createAppTheme('light')
export const swaashDarkTheme = createAppTheme('dark')
